import type { TaskItem } from '@/api/taskApi';
import { issueStatsSeverityFromPriority, issueStatsSeverityRank, mapIssueStatsStatus } from '@/utils/projectIssueStats';
import type { RiskLevel } from '@/utils/riskLevel';

export type OverviewUiStatus = '待接受' | '解决中' | '已解决' | '已延期';
export type OverviewUiSeverity = RiskLevel;

/** 设计稿指标卡顺序：解决中 → 待接受 → 已解决 → 已延期 */
export const OVERVIEW_STATUS_META: {
  key: string;
  label: OverviewUiStatus;
  color: string;
}[] = [
  { key: 'solving', label: '解决中', color: '#e6a23c' },
  { key: 'pendingAccept', label: '待接受', color: '#409eff' },
  { key: 'resolved', label: '已解决', color: '#67c23a' },
  { key: 'delayed', label: '已延期', color: '#f56c6c' },
];

export function mapTaskStatusToOverviewUi(t: TaskItem): OverviewUiStatus {
  return mapIssueStatsStatus(t);
}

export function severityFromPriority(priority: string | undefined | null): OverviewUiSeverity {
  return issueStatsSeverityFromPriority(priority);
}

export function severityRank(priority: string | undefined | null): number {
  return issueStatsSeverityRank(priority);
}

export function normalizePriority(priority: string | undefined | null): string {
  const p = String(priority ?? '').trim().toUpperCase();
  if (p === 'P0' || p === 'P1' || p === 'P2' || p === 'P3') return p;
  return 'P3';
}

export function formatDateOnly(iso: string | undefined | null): string {
  if (!iso) return '';
  const d = String(iso).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : '';
}

export function parseAssigneeIds(t: TaskItem): number[] {
  if (Array.isArray(t.assigneeUserIds) && t.assigneeUserIds.length) {
    return t.assigneeUserIds.map(Number).filter((n) => !Number.isNaN(n) && n > 0);
  }
  if (t.assigneeUserId != null && Number(t.assigneeUserId) > 0) {
    return [Number(t.assigneeUserId)];
  }
  const raw = String(t.assignee ?? '').trim();
  if (/^\d+$/.test(raw)) {
    const n = Number(raw);
    return n > 0 ? [n] : [];
  }
  return [];
}

export function resolveIssueLabel(t: TaskItem): string {
  return (
    String(t.issueNumberLabel ?? '').trim() ||
    (t.issueNumber != null && t.issueNumber > 0
      ? `问题-${String(t.issueNumber).padStart(3, '0')}`
      : '') ||
    String(t.title ?? '').trim() ||
    `任务#${t.id}`
  );
}

export function resolveIssueDescription(t: TaskItem): string {
  const desc = String(t.description ?? '').trim();
  if (desc) return desc;
  return String(t.title ?? '').trim() || '—';
}

export function resolveIssueCategory(t: TaskItem): string {
  const cat = String(t.issueCategory ?? '').trim();
  if (cat) return cat;
  const desc = String(t.description ?? '');
  if (desc.includes('【关键问题】')) return '关键问题';
  if (desc.includes('【风险】')) return '风险';
  if (desc.includes('【待确认】')) return '待确认';
  return '未分类';
}

const CATEGORY_COLORS = ['#409eff', '#9b59b6', '#36cfc9', '#e6a23c', '#f56c6c', '#67c23a'];

export function categoryBadgeColor(category: string): string {
  const s = String(category || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return CATEGORY_COLORS[h % CATEGORY_COLORS.length];
}

export async function mapPool<T, R>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (next < items.length) {
      const i = next++;
      results[i] = await worker(items[i], i);
    }
  });
  await Promise.all(runners);
  return results;
}
